// @ts-nocheck
/**
 * Homepage swipe feed controller. The deck itself lives in vhp-collection-reels.js; this file owns
 *  - the feed source (top-bar modes -> collections, fetched as cards via templates/collection.feed.liquid)
 *  - paging (the deck "clicks" #loadMoreBtn when it runs low): recipes not swiped yet first, best ranked first
 *    (assets/vhp-deck-order.js over the whole-feed index, templates/collection.feed-index.liquid), then the
 *    remaining pages for the rotation
 *  - the filters of the top bar sheet (assets/vhp-filters.js), applied client side to the loaded cards
 * Everything degrades to the server-rendered first page of the default feed.
 *
 * The same section runs above a recipe article (sections/vhp-home-feed.liquid, data-entry-card): the deck opens on
 * that recipe, the feed modes in the top bar are plain links to the homepage, and the feed memory is only written.
 * Above a collection page (data-entry-collection) it plays that collection, with the same plain links; the feed
 * memory is left alone there, so the Swipe tab still continues the homepage feed.
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpFeed) return;

  const DEFAULT_MODE = 'alle-rezepte';

  // The standing filters of the top bar sheet (assets/vhp-filters.js).
  const hasFilters = () => Boolean(window.vhpFilters?.count());

  // Explore set: a fixed list of recipes chosen on the Explore tab; the feed filters don't apply to it.
  let customSet = null;
  // Label for a collection opened from Explore (no feed tab of its own), shown in the set pill.
  let modeLabel = '';

  // Liked product ids for "Noch nicht geliked", read once per change of the likes.
  let likedIds = null;
  window.addEventListener('vhp:likes-changed', () => (likedIds = null));
  const isLiked = (id) => {
    if (!likedIds) {
      try {
        likedIds = new Set((window.vhpPlannerLikes?.read().likes.items || []).map((item) => String(item.id)));
      } catch (_) {
        likedIds = new Set();
      }
    }
    return likedIds.has(String(id));
  };

  const cardNum = (value) => (value === '' || value == null || !Number.isFinite(Number(value)) ? null : Number(value));

  // Meal, "Ohne" and the extras need the search core's rows: until they are in, those filters let the card through
  // (vhp-filters.js loads them and fires vhp:filters-changed, which re-checks the deck).
  const accept = (card) => {
    if (customSet || !hasFilters()) return true;
    const api = window.vhpFilters;
    const d = card.el?.dataset || {};
    const filters = api.get();
    const rec = {
      id: d.id,
      protein: cardNum(d.protein),
      time: cardNum(d.time),
      kcal: cardNum(d.kcal),
      liked: Boolean(d.id) && filters.props.includes('fresh') && isLiked(d.id),
    };
    if (api.needsFacts(filters) && !window.vhpSearchCore?.data()) {
      api.ensureFacts();
      return api.passes(rec, { ...filters, meal: [], avoid: [], props: filters.props.filter((p) => p === 'fresh') });
    }
    return api.passes(rec, filters);
  };

  const getRoot = () => document.querySelector('[data-vhp-feed]');
  // The feed of this page, also while another tab is shown in-page (assets/app-router.js keeps it out of the document).
  let feedRoot = getRoot();
  const ownRoot = () => (feedRoot ||= getRoot());
  // Recipe article or collection opened in the feed: the page is the recipe / collection, so the feed modes leave it
  // for the homepage.
  const isEntry = (root) => Boolean(root?.dataset.entryCard || root?.dataset.entryCollection);
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------- feed source */

  const pageCache = new Map();
  let modeToken = 0;
  let paging = false;

  // Caches the request itself, so a prefetch in flight is reused instead of fetched twice.
  const fetchCards = (handle, page) => {
    const url = `${rootUrl()}collections/${encodeURIComponent(handle)}?view=feed&page=${page}`;
    if (pageCache.has(url)) return pageCache.get(url);
    const request = (async () => {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`feed ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const holder = doc.querySelector('#vhpFeedCards');
      return {
        html: holder?.querySelector('#recipeGrid')?.innerHTML || '',
        next: holder?.dataset.nextPage || '',
      };
    })();
    pageCache.set(url, request);
    request.catch(() => pageCache.delete(url));
    return request;
  };

  // The deck pages 12 cards at a time; for a returning visitor most of them are already swiped, so the next
  // page is fetched as soon as one lands and "Neue Rezepte werden geladen" rarely waits on the network.
  const prefetch = (handle, page) => {
    if (handle && page) fetchCards(handle, page).catch(() => {});
  };

  const setNext = (root, next) => {
    const wrap = root.querySelector('#loadMoreWrap');
    if (!wrap) return;
    wrap.dataset.nextPage = next || '';
    wrap.style.display = next ? '' : 'none';
  };

  const syncTabs = (handle) => {
    document.querySelectorAll('[data-feed-handle]').forEach((tab) => {
      const on = tab.dataset.feedHandle === handle;
      tab.classList.toggle('is-active', on);
      if (on) tab.setAttribute('aria-current', 'page');
      else tab.removeAttribute('aria-current');
      if (on && tab.scrollIntoView) tab.scrollIntoView({ inline: 'center', block: 'nearest' });
    });
  };

  const setMode = async (handle, label = '') => {
    const root = getRoot();
    if (!root || !handle || (root.dataset.feedMode === handle && !customSet)) return;
    customSet = null;
    modeLabel = label;
    paintSetPill(root);
    const overlay = root.querySelector('[data-vhp-reel-overlay]');
    const grid = root.querySelector('#recipeGrid');
    if (!grid) return;
    const token = ++modeToken;
    syncTabs(handle);
    overlay?.classList.add('is-feed-loading');
    try {
      const { html, next } = await fetchCards(handle, 1);
      if (token !== modeToken) return;
      root.dataset.feedMode = handle;
      root.dataset.collectionHandle = handle;
      grid.innerHTML = html;
      requested.clear();
      setNext(root, next);
      prefetch(handle, Number(next));
      window.vhpReels?.reload(root);
      // Not when another tab took over meanwhile (assets/app-router.js): the URL is that tab's now.
      if (root.isConnected) {
        try {
          history.replaceState(history.state, '', handle === DEFAULT_MODE ? rootUrl() : `${rootUrl()}?feed=${handle}`);
        } catch (_) {}
      }
      if (typeof window.vhpTrack === 'function') window.vhpTrack('feed_mode', { mode: handle });
    } catch (_) {
      if (token === modeToken) syncTabs(root.dataset.feedMode);
    } finally {
      if (token === modeToken) overlay?.classList.remove('is-feed-loading');
    }
  };

  /* ---------------------------------------------------------------- Explore set */

  const SET_KEY = 'vhp_explore_set';
  const SET_MAX_AGE = 6 * 3600 * 1000;
  const SET_CHUNK = 5;

  const fetchCard = async (handle) => {
    const url = `${rootUrl()}products/${encodeURIComponent(handle)}?view=card`;
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return '';
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return doc.querySelector('.recipe-card')?.outerHTML || '';
    } catch (_) {
      return '';
    }
  };

  /* ---------------------------------------------------------------- unseen recipes */

  // For a returning visitor most pages hold only swiped recipes. One request lists the whole feed
  // (templates/collection.feed-index.liquid); paging then fetches the recipes not swiped yet first, as single
  // cards, and pages through the rest only to fill the rotation.
  const UNSEEN_CHUNK = 4;
  const SKIP_EXPIRE_MS = 30 * 86400000;
  const indexCache = new Map();
  // Handles already fetched as single cards since the grid was last replaced.
  const requested = new Set();

  const fetchIndex = (handle) => {
    if (indexCache.has(handle)) return indexCache.get(handle);
    const request = (async () => {
      const items = [];
      for (let page = 1; page; ) {
        const url = `${rootUrl()}collections/${encodeURIComponent(handle)}?view=feed-index&page=${page}`;
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`feed index ${response.status}`);
        const holder = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('#vhpFeedIndex');
        if (!holder) throw new Error('feed index missing');
        items.push(...JSON.parse(holder.textContent || '[]'));
        page = Number(holder.dataset.nextPage) || 0;
      }
      return items;
    })();
    indexCache.set(handle, request);
    request.catch(() => indexCache.delete(handle));
    return request;
  };

  // The whole feed in deck order (assets/vhp-deck-order.js, with this browser's likes / skips / goal): { cards:
  // [{ id, handle, slot, pub, tags, title, ex, kcal, protein }], shuffled, personal }. Plain index order without
  // the module.
  const rankCache = new Map();
  const rankedIndex = (handle) => {
    if (rankCache.has(handle)) return rankCache.get(handle);
    const request = fetchIndex(handle).then((items) => {
      const cards = items.map(([id, h, slot, pub, tags, title, ex, kcal, protein]) => ({ id, handle: h, slot, pub, tags, title, ex: ex === 1, kcal, protein }));
      const order = window.vhpDeckOrder;
      if (!order) return { cards, shuffled: false, personal: false };
      const seed = order.seed(order.browserSeed(), 'deck');
      return order.rank(cards, { stats: order.pageStats(), seed, now: Date.now(), taste: order.browserTaste() });
    });
    rankCache.set(handle, request);
    request.catch(() => rankCache.delete(handle));
    return request;
  };

  // For the deck (assets/vhp-collection-reels.js): { ids, shuffled, personal } of this feed, or null (Explore set,
  // no index). opts.fresh: rank again with the swipes since (the index itself stays cached).
  const ranking = async (root, opts) => {
    const mode = root?.dataset.feedMode;
    if (!mode || customSet || mode === 'explore-set' || !window.vhpDeckOrder) return null;
    if (opts?.fresh) rankCache.delete(mode);
    try {
      const { cards, shuffled, personal } = await rankedIndex(mode);
      return { ids: cards.map((card) => card.id), shuffled, personal };
    } catch (_) {
      return null;
    }
  };

  // Puts the next few not-yet-swiped recipes into the grid. False when there are none (or no index), so the
  // caller pages instead.
  const loadUnseen = async (root, grid, mode, token) => {
    const api = window.vhpPlannerLikes;
    if (!api) return false;
    let items;
    try {
      items = (await rankedIndex(mode)).cards;
    } catch (_) {
      return false;
    }
    for (;;) {
      if (token !== modeToken) return true;
      const doc = api.read();
      // Skips older than SKIP_EXPIRE_MS count as not seen, after the never-seen ones (as in vhp-collection-reels.js).
      const staleIds = new Set(
        doc.skips.items.filter((item) => Date.now() - (Date.parse(item.at || '') || Date.now()) > SKIP_EXPIRE_MS).map((item) => String(item.id))
      );
      const swiped = new Set([...doc.likes.items, ...doc.skips.items].map((item) => String(item.id)).filter((id) => !staleIds.has(id)));
      // Reel layout: cards scrolled past (seen, neither liked nor skipped; vhp-collection-reels.js SEEN_KEY) count as
      // swiped here too, so the never-seen ones load first.
      try {
        const seen = JSON.parse(localStorage.getItem('vhp_feed_seen_v1') || 'null') || {};
        const known = new Set([...doc.likes.items, ...doc.skips.items].map((item) => String(item.id)));
        Object.keys(seen).forEach((id) => {
          if (!known.has(id) && Date.now() - Number(seen[id]) < SKIP_EXPIRE_MS) swiped.add(id);
        });
      } catch (_) {}
      const inGrid = new Set([...grid.querySelectorAll('[data-id]')].map((el) => el.dataset.id));
      // Best ranked first.
      const open = items.filter(({ id, handle }) => !swiped.has(id) && !inGrid.has(id) && !requested.has(handle));
      const handles = [...open.filter(({ id }) => !staleIds.has(id)), ...open.filter(({ id }) => staleIds.has(id))]
        .slice(0, UNSEEN_CHUNK)
        .map(({ handle }) => handle);
      if (!handles.length) return false;
      handles.forEach((handle) => requested.add(handle));
      const html = (await Promise.all(handles.map(fetchCard))).join('');
      if (token !== modeToken) return true;
      // Nothing to show (hidden for this customer, gone): try the next few, the deck waits on a grid change.
      if (!html) continue;
      grid.insertAdjacentHTML('beforeend', html);
      return true;
    }
  };

  const loadSetChunk = async (root, n) => {
    const grid = root.querySelector('#recipeGrid');
    if (!customSet || !grid) return;
    const slice = customSet.handles.slice(customSet.pos, customSet.pos + n);
    customSet.pos += slice.length;
    const htmls = await Promise.all(slice.map(fetchCard));
    if (!customSet) return;
    grid.insertAdjacentHTML('beforeend', htmls.join(''));
    setNext(root, customSet.pos < customSet.handles.length ? 'set' : '');
  };

  function paintSetPill(root) {
    const pill = root.querySelector('[data-feed-set]');
    if (!pill) return;
    pill.hidden = !customSet && !modeLabel;
    const label = pill.querySelector('[data-feed-set-label]');
    if (label && customSet) label.textContent = customSet.label || tr('reels.from_explore');
    else if (label && modeLabel) label.textContent = modeLabel;
  }

  // payload: { handles: [...], start: handle, label }. Starts at `start`, then the rest of the list in order.
  const startSet = async (payload) => {
    const root = getRoot();
    const grid = root?.querySelector('#recipeGrid');
    if (!root || !grid || !payload?.handles?.length) return;
    const all = payload.handles.filter((h, i, list) => typeof h === 'string' && list.indexOf(h) === i);
    const at = Math.max(0, all.indexOf(payload.start));
    const ordered = all.slice(at).concat(all.slice(0, at));
    const token = ++modeToken;
    const overlay = root.querySelector('[data-vhp-reel-overlay]');
    overlay?.classList.add('is-feed-loading');
    syncTabs(null);
    customSet = { handles: ordered, pos: 0, label: payload.label || tr('reels.from_explore') };
    root.dataset.feedMode = 'explore-set';
    grid.innerHTML = '';
    try {
      await loadSetChunk(root, SET_CHUNK);
      if (token !== modeToken) return;
      paintSetPill(root);
      const found = window.vhpReels?.reload(root, { keepOrder: true, pinHandle: ordered[0] });
      if (found === false && window.vhpRecipeSheet) {
        // The recipe has no swipe card (not a plannable meal): show it in the sheet instead.
        window.vhpRecipeSheet.open(`/blogs/rezepte/${encodeURIComponent(ordered[0])}`);
      }
      if (typeof window.vhpTrack === 'function') window.vhpTrack('explore_open_feed', { start: ordered[0], size: ordered.length });
    } finally {
      if (token === modeToken) overlay?.classList.remove('is-feed-loading');
    }
  };

  const readSet = () => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(SET_KEY) || 'null');
      sessionStorage.removeItem(SET_KEY);
      if (!raw || !Array.isArray(raw.handles) || Date.now() - Number(raw.at || 0) > SET_MAX_AGE) return null;
      return raw;
    } catch (_) {
      return null;
    }
  };

  const loadMore = async () => {
    const root = getRoot();
    if (customSet && root) {
      const btn = root.querySelector('#loadMoreBtn');
      if (paging || !btn) return;
      paging = true;
      btn.disabled = true;
      try {
        await loadSetChunk(root, SET_CHUNK);
      } finally {
        paging = false;
        btn.disabled = false;
      }
      return;
    }
    const wrap = root?.querySelector('#loadMoreWrap');
    const btn = root?.querySelector('#loadMoreBtn');
    const grid = root?.querySelector('#recipeGrid');
    const page = Number(wrap?.dataset.nextPage);
    if (!root || !grid || !btn || !page || paging) return;
    paging = true;
    btn.disabled = true;
    const token = modeToken;
    const mode = root.dataset.feedMode;
    try {
      if (await loadUnseen(root, grid, mode, token)) return;
      const { html, next } = await fetchCards(mode, page);
      if (token !== modeToken) return;
      grid.insertAdjacentHTML('beforeend', html);
      setNext(root, next);
      prefetch(mode, Number(next));
    } catch (_) {
      setNext(root, '');
    } finally {
      paging = false;
      btn.disabled = false;
    }
  };

  /* ---------------------------------------------------------------- filters */

  // The sheet itself is assets/vhp-filters.js; the deck re-checks its cards whenever the filters change.
  window.addEventListener('vhp:filters-changed', () => {
    const root = getRoot();
    if (root) window.vhpReels?.refresh(root);
  });

  /* ---------------------------------------------------------------- collection sheet */

  // The next page of the collection sheet's tiles (sections/vhp-collection-sheet.liquid), appended in place. The
  // link is a real ?page= link: on any failure it is simply followed.
  let sheetObserver = null;
  const loadSheetPage = async (link) => {
    if (link.dataset.loading) return;
    link.dataset.loading = '1';
    link.classList.add('is-loading');
    const href = link.href;
    try {
      const url = new URL(href, location.href);
      url.hash = '';
      url.searchParams.set('section_id', link.dataset.section);
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`sheet page ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = link.closest('[data-collection-sheet]')?.querySelector('.vhp-collection-sheet__grid');
      if (!grid) throw new Error('sheet grid');
      grid.append(...doc.querySelectorAll('.vhp-collection-sheet__grid > li'));
      const nextHref = doc.querySelector('[data-collection-next]')?.getAttribute('href');
      sheetObserver?.unobserve(link);
      if (!nextHref) {
        link.remove();
        return;
      }
      link.href = new URL(nextHref, location.href).toString();
      delete link.dataset.loading;
      link.classList.remove('is-loading');
      // Observed again, so a page that leaves the link in view goes straight on to the next one.
      sheetObserver?.observe(link);
    } catch (_) {
      location.href = href;
    }
  };

  const watchSheet = () => {
    const links = document.querySelectorAll('[data-collection-next]');
    if (!links.length || !('IntersectionObserver' in window)) return;
    sheetObserver ||= new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && loadSheetPage(entry.target)),
      { rootMargin: '0px 0px 600px 0px' }
    );
    links.forEach((link) => sheetObserver.observe(link));
  };

  /* ---------------------------------------------------------------- wiring */

  window.vhpFeed = { accept, hasFilters, setMode, ranking };

  /* ---------------------------------------------------------------- feed memory */

  // Leaving the feed (another tab, a recipe page) and coming back within the session lands on the same
  // recipe: the loaded cards, the deck order and the current card are kept in sessionStorage. The deck
  // (vhp-collection-reels.js) runs after this file and picks the order up from window.vhpFeedRestore.
  const SNAP_KEY = 'vhp_feed_snapshot_v1';
  const SNAP_MAX_AGE = 30 * 60 * 1000;

  const saveSnapshot = () => {
    const root = ownRoot();
    const grid = root?.querySelector('#recipeGrid');
    if (root?.dataset.entryCollection) return;
    const deck = root && window.vhpReels?.snapshot?.(root);
    try {
      if (!root || !grid || !deck || customSet) {
        sessionStorage.removeItem(SNAP_KEY);
        return;
      }
      sessionStorage.setItem(
        SNAP_KEY,
        JSON.stringify({
          mode: root.dataset.feedMode,
          html: grid.innerHTML,
          next: root.querySelector('#loadMoreWrap')?.dataset.nextPage || '',
          order: deck.order,
          current: deck.current,
          at: Date.now(),
        })
      );
    } catch (_) {
      /* quota / private mode: the feed simply starts fresh */
    }
  };

  const restoreSnapshot = () => {
    const root = getRoot();
    const grid = root?.querySelector('#recipeGrid');
    if (!root || !grid || isEntry(root)) return;
    const params = new URLSearchParams(location.search);
    if (params.get('set') === 'explore') return;
    // A reload means "fresh feed" (and gets the preloaded first card); the memory is for tab switches.
    if (performance.getEntriesByType?.('navigation')[0]?.type === 'reload') {
      try {
        sessionStorage.removeItem(SNAP_KEY);
      } catch (_) {}
      return;
    }
    let snap = null;
    try {
      snap = JSON.parse(sessionStorage.getItem(SNAP_KEY) || 'null');
    } catch (_) {
      snap = null;
    }
    if (!snap || !snap.html || !Array.isArray(snap.order) || Date.now() - Number(snap.at || 0) > SNAP_MAX_AGE) return;
    if (snap.mode !== (params.get('feed') || root.dataset.feedMode)) return;
    grid.innerHTML = snap.html;
    root.dataset.feedMode = snap.mode;
    root.dataset.collectionHandle = snap.mode;
    setNext(root, snap.next);
    syncTabs(snap.mode);
    window.vhpFeedRestore = { order: snap.order, current: snap.current || null };
  };

  restoreSnapshot();
  window.addEventListener('pagehide', saveSnapshot);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveSnapshot();
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-feed-handle]');
    if (tab && getRoot() && !isEntry(getRoot())) {
      event.preventDefault();
      setMode(tab.dataset.feedHandle);
      return;
    }

    if (target.closest('[data-feed-set-exit]') && getRoot()) {
      event.preventDefault();
      setMode(DEFAULT_MODE);
      return;
    }

    // Collection page (sections/vhp-collection-sheet.liquid): the sheet's peeking top scrolls it up, a recipe tile
    // opens the recipe sheet over the page (the link stays for crawlers and new tabs).
    const sheet = target.closest('[data-collection-sheet]');
    if (sheet) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      // Back up to the deck in one jump: a smooth scroll over the booting deck stalls halfway (its layout moves
      // while the animation runs), so this one ignores the page's scroll-behavior.
      const jumpToDeck = () => {
        const html = document.documentElement;
        const was = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, 0);
        html.style.scrollBehavior = was;
      };
      // "Swipen" in the sheet's head (list first, docs/CR_AUDIT.md): back up to the deck.
      if (target.closest('[data-vhp-sheet-swipe]')) {
        jumpToDeck();
        if (typeof window.vhpTrack === 'function') window.vhpTrack('collection_sheet_swipe', {});
        return;
      }
      const peek = target.closest('[data-vhp-entry-peek]');
      if (peek) {
        // Already at the top (the list opened first): the peek leads back to the deck instead.
        if (peek.getBoundingClientRect().top < 8) {
          jumpToDeck();
          return;
        }
        peek.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        if (typeof window.vhpTrack === 'function') window.vhpTrack('collection_entry_peek', {});
        return;
      }
      const tile = target.closest('a.ex-tile[href]');
      const plain = !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0;
      if (tile && plain && window.vhpRecipeSheet) {
        event.preventDefault();
        window.vhpRecipeSheet.open(tile.getAttribute('href'), { media: true, source: 'collection' });
        if (typeof window.vhpTrack === 'function') window.vhpTrack('collection_tile_open', { handle: tile.dataset.handle });
        return;
      }
      const next = target.closest('[data-collection-next]');
      if (next && plain) {
        event.preventDefault();
        loadSheetPage(next);
        return;
      }
    }

    if (target.closest('#loadMoreBtn') && getRoot()?.contains(target)) {
      loadMore();
    }
  });

  const init = () => {
    const root = getRoot();
    if (!root) return;
    if (isEntry(root)) {
      watchSheet();
      if (typeof window.vhpTrack === 'function') {
        if (root.dataset.entryCard) window.vhpTrack('recipe_entry_feed', { id: root.dataset.entryCard });
        else window.vhpTrack('collection_entry_feed', { handle: root.dataset.entryCollection });
      }
      warmUp();
      return;
    }
    const params = new URLSearchParams(location.search);
    if (params.get('set') === 'explore') {
      const payload = readSet();
      try {
        history.replaceState(history.state, '', rootUrl());
      } catch (_) {}
      if (payload) {
        startSet(payload);
        return;
      }
    }
    const wanted = params.get('feed');
    if (wanted && wanted !== root.dataset.feedMode) {
      // A feed tab, or any collection opened from Explore (its title comes along as ?fl=).
      const hasTab = Boolean(document.querySelector(`[data-feed-handle="${CSS.escape(wanted)}"]`));
      if (hasTab) setMode(wanted);
      else if (/^[a-z0-9-]+$/.test(wanted)) setMode(wanted, (params.get('fl') || '').slice(0, 40) || tr('reels.from_explore'));
    }
    warmUp();
  };

  // Once the first video has had the connection to itself: the next page of this feed and page 1 of every
  // tab, so paging and tab switches come from the cache.
  const warmUp = () => {
    const run = () => {
      const root = getRoot();
      if (!root || customSet) return;
      prefetch(root.dataset.feedMode, Number(root.querySelector('#loadMoreWrap')?.dataset.nextPage));
      if (root.querySelector('#loadMoreWrap')?.dataset.nextPage) fetchIndex(root.dataset.feedMode).catch(() => {});
      if (isEntry(root)) return;
      document.querySelectorAll('[data-feed-handle]').forEach((tab) => {
        if (tab.dataset.feedHandle !== root.dataset.feedMode) prefetch(tab.dataset.feedHandle, 1);
      });
    };
    const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 3000 }) : run());
    const later = () => window.setTimeout(idle, 2500);
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
